import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinnerThird } from '@fortawesome/sharp-solid-svg-icons';
import { isNullOrUndefined } from 'eone-ui/functions/general';
import { TablePrompts } from 'eone-ui/tables/table';
import { TableFieldType, TableSortDirection } from 'eone-ui/tables/table-constants';
import { TableHeaderRow } from 'eone-ui/tables/table-header';
import React, { useEffect, useState } from 'react';
import { TableRow } from './table-row';
import './tables.css';

function TableFooter(props) {
  if (!props.Text) {
    return null;
  }

  return <div className="eone-ui-table-counter">{props.Text}</div>;
}

function TableContainer(props) {
  const getContainerClass = () => {
    let containerClass = 'eone-ui-table-container';
    if (props.BodyHeight) {
      containerClass = 'eone-ui-table-container-with-height';
    }
    if (props.HorizontalScroll) {
      containerClass += ' eone-ui-table-container-horizontal-scroll';
    }
    return containerClass;
  };

  return (
    <div className={getContainerClass()} style={{ height: props.BodyHeight }}>
      {props.children}
      <TableFooter Text={props.FooterText} />
    </div>
  );
}

function MasterDetailTable(props) {
  const [editing, setEditing] = useState(false);
  const [rowSorting, setRowSorting] = useState<any[]>([]);

  useEffect(() => {
    if (!editing) {
      generateRows();
    }
  }, [props.TableState, props.Rows, props.Columns, props.IdField]);

  useEffect(() => {
    if (!editing) generateRows();
  }, [editing]);

  const changeHandler = (id, fieldName, value) => {
    if (typeof props.ChangeValue === 'function') {
      setEditing(true);
      props.ChangeValue(id, fieldName, value);
    }
  };

  const updateHandler = (id, fieldName, value) => {
    if (typeof props.UpdateValue === 'function') {
      props.UpdateValue(id, fieldName, value);
      setEditing(false);
    }
  };

  const selectRow = (idField) => {
    if (typeof props.SelectRow === 'function') {
      props.SelectRow(idField);
    }
  };

  const generateRows = () => {
    let rows = searchRows();
    rows = filterRows(rows);
    sortRows(rows);
  };

  const isSearched = (row, column) => {
    if (column.FieldName in row && row[column.FieldName]) {
      // todo - handle selects
      // todo - handle integers (should only be equal)
      return String(row[column.FieldName])
        .toLowerCase()
        .includes(props.TableState.Search.toLowerCase());
    }
    return false;
  };

  const isRowSearched = (row, columns) => {
    for (let column = 0, numberOfColumns = columns.length; column < numberOfColumns; column++) {
      if (isSearched(row, columns[column])) {
        return true;
      }
    }
    return false;
  };

  const searchRows = () => {
    if (!props.TableState?.Search) return props.Rows;
    const searchableColumns = props.Columns.filter((column) => column.Searchable);
    if (searchableColumns.length === 0) return props.Rows;
    const rows: any[] = [];
    for (let row = 0, numberOfRows = props.Rows.length; row < numberOfRows; row++) {
      if (isRowSearched(props.Rows[row], searchableColumns)) {
        rows.push(props.Rows[row]);
      }
    }
    return rows;
  };

  const isFiltered = (row, fieldName) => {
    if (
      fieldName in row &&
      row[fieldName] !== undefined &&
      props.TableState?.FilterValues[fieldName].length > 0
    ) {
      return props.TableState.FilterValues[fieldName].includes(row[fieldName]);
    }
    return false;
  };

  const isRowFiltered = (row) => {
    const fieldNames = Object.getOwnPropertyNames(props.TableState.FilterValues);
    if (fieldNames.length === 0) return true;

    let filtered = true;
    for (let i = 0, len = fieldNames.length; i < len; i++) {
      if (
        props.TableState.FilterValues[fieldNames[i]] &&
        props.TableState.FilterValues[fieldNames[i]].length > 0 &&
        !isFiltered(row, fieldNames[i])
      ) {
        filtered = false;
      }
    }
    return filtered;
  };

  const filterRows = (rows) => {
    if (!props.TableState?.FilterValues) return rows;
    const filterRows: any[] = [];
    for (let row = 0, numberOfRows = rows.length; row < numberOfRows; row++) {
      if (isRowFiltered(rows[row])) {
        filterRows.push(rows[row]);
      }
    }
    return filterRows;
  };

  const getSelectDisplayValue = (row, column) => {
    const value = row[column.FieldName];
    if (!value) return '';
    if (!column.Options) return value;
    const options = column.Options || row[column.RowOptions] || [];
    const option = options.find((item) => item.Key === value);
    if (!option) return value?.toString().toLowerCase();
    if (!option.Value) return value?.toString().toLowerCase();
    return option.Value?.toString().toLowerCase();
  };

  const sortRows = (rows) => {
    const collator = new Intl.Collator(props.Locale, { caseFirst: 'upper' });
    if (!props.TableState?.Sort || !props.TableState?.Sort.FieldName) {
      setSorting(rows);
      return;
    }
    const sortColumn = props.Columns.find(
      (column) => column.FieldName === props.TableState.Sort.FieldName,
    );
    if (!sortColumn) {
      setSorting(rows);
      return;
    }
    switch (sortColumn.FieldType) {
      case TableFieldType.Select:
        if (props.TableState.Sort.Direction === TableSortDirection.Descending) {
          rows.sort((a, b) => {
            return -collator.compare(
              getSelectDisplayValue(a, sortColumn),
              getSelectDisplayValue(b, sortColumn),
            );
          });
        } else {
          rows.sort((a, b) => {
            return collator.compare(
              getSelectDisplayValue(a, sortColumn),
              getSelectDisplayValue(b, sortColumn),
            );
          });
        }
        break;
      case TableFieldType.Checkbox:
        if (props.TableState.Sort.Direction === TableSortDirection.Descending) {
          rows.sort((a, b) => {
            return Boolean(a[sortColumn.FieldName]) < Boolean(b[sortColumn.FieldName]) ? 1 : -1;
          });
        } else {
          rows.sort((a, b) => {
            return Boolean(a[sortColumn.FieldName]) > Boolean(b[sortColumn.FieldName]) ? 1 : -1;
          });
        }
        break;
      case TableFieldType.Integer:
        if (props.TableState.Sort.Direction === TableSortDirection.Descending) {
          rows.sort((a, b) => {
            return Number.parseInt(a[sortColumn.FieldName]) <
              Number.parseInt(b[sortColumn.FieldName])
              ? 1
              : -1;
          });
        } else {
          rows.sort((a, b) => {
            return Number.parseInt(a[sortColumn.FieldName]) >
              Number.parseInt(b[sortColumn.FieldName])
              ? 1
              : -1;
          });
        }
        break;
      case TableFieldType.Date:
        if (props.TableState.Sort.Direction === TableSortDirection.Descending) {
          rows.sort((a, b) => {
            if (!a && !b) return 0;
            const defaultDate = new Date(-8640000000000000); // Minimum date value
            const dateA = a[sortColumn.FieldName] ? new Date(a[sortColumn.FieldName]) : defaultDate;
            const dateB = b[sortColumn.FieldName] ? new Date(b[sortColumn.FieldName]) : defaultDate;
            return dateB.getTime() - dateA.getTime();
          });
        } else {
          rows.sort((a, b) => {
            if (!a && !b) return 0;
            const defaultDate = new Date(-8640000000000000); // Minimum date value
            const dateA = a[sortColumn.FieldName] ? new Date(a[sortColumn.FieldName]) : defaultDate;
            const dateB = b[sortColumn.FieldName] ? new Date(b[sortColumn.FieldName]) : defaultDate;
            return dateA.getTime() - dateB.getTime();
          });
        }
        break;
      default:
        if (props.TableState.Sort.Direction === TableSortDirection.Descending) {
          rows.sort((a, b) => {
            return -1 * collator.compare(a[sortColumn.FieldName], b[sortColumn.FieldName]);
          });
        } else {
          rows.sort((a, b) => {
            return collator.compare(a[sortColumn.FieldName], b[sortColumn.FieldName]);
          });
        }
        break;
    }
    setSorting(rows);
  };

  const setSorting = (rows) => {
    const newRowSorting: any[] = [];
    if (rows) {
      for (let i = 0, length = rows.length; i < length; i++) {
        newRowSorting.push(rows[i][props.IdField]);
      }
    }
    setRowSorting(newRowSorting);
  };

  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const handleExpand = (id) => {
    setExpandedRows((prevState) => ({ ...prevState, [id]: !prevState[id] }));
  };

  const getSubRows = (row, index) => {
    console.log('[getSubRows] props =', props);
    const currentDepth = props.Depth;
    const currentSubRowsField = props.SubRowFields[currentDepth];

    if (row[currentSubRowsField]?.length > 0) return row[currentSubRowsField];
    else if (props.GetSubRows) return props.GetSubRows(row, index);
    else return [];
  };

  const renderRow = (row, index) => {
    const currentDepth = props.Depth;
    const currentSubRowsField = props.SubRowFields[currentDepth];
    const currentIdField = props.IdFields[currentDepth];
    const currentButtons = !props.RowButtonsTest ? [] : (props.RowButtonsTest[currentDepth] ?? []);
    const currentColumns = props.ColumnsTest[currentDepth];
    const hasSubRows = row[currentSubRowsField]?.length > 0 || props.HasSubRows;

    return (
      <React.Fragment key={index}>
        <TableRow
          Row={row}
          Columns={currentColumns}
          SelectedColumns={props.TableState?.Columns}
          SelectRow={selectRow}
          Selected={
            currentIdField &&
            !isNullOrUndefined(props.SelectedRowId) &&
            row[currentIdField] === props.SelectedRowId
          }
          Buttons={currentButtons}
          IdField={currentIdField}
          Locale={props.Locale}
          ChangeValue={changeHandler}
          UpdateValue={updateHandler}
          ButtonWidth={buttonWidth + 12}
          DataTestId={props.DataTestId}
          ShowExpand={hasSubRows}
          IsExpanded={expandedRows[row[currentIdField]] == true}
          HandleExpand={handleExpand}
        />
        {hasSubRows && expandedRows[row[currentIdField]] && (
          <tr>
            <td
              style={{ paddingLeft: (currentDepth + 1) * (props.ChildPadding ?? 50) }}
              colSpan={100}
            >
              <MasterDetailTable
                ChangeValue={props.ChangeValue}
                IdFields={props.IdFields}
                SubRowFields={props.SubRowFields}
                ColumnsTest={props.ColumnsTest}
                Depth={currentDepth + 1}
                Columns={currentColumns}
                Rows={getSubRows(row, index)}
                Buttons={props.RowButtons}
                RowButtonsTest={props.RowButtonsTest}
              />
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  const getFilterValues = () => {
    const filterValues: Record<string, any> = {};
    if (props.Columns) {
      for (let i = 0, len = props.Columns.length; i < len; i++) {
        // only get filter values for selected columns
        if (
          props.Columns[i].Filterable &&
          (!props.TableState?.Columns ||
            props.TableState.Columns.length === 0 ||
            props.TableState.Columns.includes(props.Columns[i].FieldName))
        ) {
          const columnFilterValues = getColumnFilterValues(props.Columns[i]);
          columnFilterValues.sort((a, b) => {
            return a.Value?.toLowerCase() > b.Value?.toLowerCase() ? 1 : -1;
          });
          filterValues[props.Columns[i].FieldName] = columnFilterValues;
        }
      }
    }
    return filterValues;
  };

  const isColumnFilterValueSelected = (fieldName, value) => {
    if (!props.TableState?.FilterValues) return false;
    if (!(fieldName in props.TableState.FilterValues)) return false;
    if (!props.TableState.FilterValues[fieldName]) return false;
    return props.TableState.FilterValues[fieldName].includes(value);
  };

  const getColumnFilterValues = (column) => {
    let filterValues: { Key: any; Value: any }[] = [];
    switch (column.FieldType) {
      case TableFieldType.Select:
        filterValues = getColumnFilterSelectValues(column);
        break;
      case TableFieldType.Checkbox:
        filterValues = [
          { Key: false, Value: 'No' },
          { Key: true, Value: 'Yes' },
        ];
        break;
      case TableFieldType.Date:
        // todo
        break;
      case TableFieldType.Integer:
        // todo
        break;
      default:
        filterValues = getColumnFilterStringValues(column);
        break;
    }

    return filterValues.map((filterValue) => {
      return {
        Key: filterValue.Key,
        Value: filterValue.Value,
        Selected: isColumnFilterValueSelected(column.FieldName, filterValue.Key),
      };
    });
  };

  const getColumnFilterSelectValues = (column) => {
    // todo - remove unused values
    // todo - add none if blank values exist
    // todo - sort options
    let options = column.Options.slice(0);
    options = options.filter((option) => option.Key?.toString().substring(0) !== '-'); // remove separators

    return options;
  };

  const getColumnFilterStringValues = (column) => {
    const filterValues: any[] = [];
    let includeNone = false;
    for (let i = 0, len = props.Rows.length; i < len; i++) {
      const value = props.Rows[i][column.FieldName];
      if (value) {
        if (!filterValues.includes(value)) {
          filterValues.push(value);
        }
      } else {
        includeNone = true;
      }
    }
    if (includeNone) {
      // todo
    }
    return filterValues.map((filterValue, index) => {
      console.log(index);
      return {
        Key: filterValue,
        Value: filterValue,
      };
    });
  };

  const getFooterText = (filteredRowCount, rowCount) => {
    if (props.FooterText) return props.FooterText;
    if (props.ShowRecordCount) {
      if (filteredRowCount !== rowCount) {
        if (rowCount === 1) {
          return `${filteredRowCount} of ${rowCount} ${props.ItemName}`;
        } else {
          return `${filteredRowCount} of ${rowCount} ${props.ItemPlural}`;
        }
      } else {
        if (filteredRowCount === 1) {
          return `${filteredRowCount} ${props.ItemName}`;
        } else {
          return `${filteredRowCount} ${props.ItemPlural}`;
        }
      }
    }
    return '';
  };

  const getTableClass = () => {
    let tableClass = 'eone-ui-table';
    if (props.HorizontalScroll) {
      tableClass += ' eone-ui-table-horizontal-scroll';
    }
    return tableClass;
  };

  if (props.Visible !== undefined && !props.Visible) {
    return null;
  }

  const footerText = getFooterText(rowSorting.length, props.Rows?.length ?? 0);
  const currentDepthButtons =
    props.Buttons ?? (props.RowButtonsTest ? props.RowButtonsTest[props.Depth] : undefined);
  const maxRowButtonCount = () => {
    let visibleButtonCount = 0;
    if (currentDepthButtons) {
      for (const btn of currentDepthButtons) {
        if (btn.Hidden != undefined && btn.Hidden != '') {
          for (const row of props.Rows) {
            const hidden = row[btn.Hidden];
            if (!hidden) {
              visibleButtonCount++;
              break;
            }
          }
        } else visibleButtonCount++;
      }
    }
    return visibleButtonCount;
  };

  let buttonWidth = 0;
  if (currentDepthButtons) {
    const buttonLength = maxRowButtonCount();
    //console.log("[table] maxRowButtonCount =", buttonLength);
    buttonWidth = buttonLength > 0 ? 44 + (buttonLength - 1) * 43 : 0;
  }

  if (props.Loading) {
    return (
      <div className="eone-ui-table-loading eone-ui-table-master-container">
        <div>
          <FontAwesomeIcon icon={faSpinnerThird} spin />
          <label className="eone-ui-table-loading-prompt">
            {props.LoadingMessage || 'Loading...'}
          </label>
        </div>
      </div>
    );
  }

  if (!props.Rows || props.Rows.length === 0) {
    return (
      <div className="eone-ui-table-master-container">
        <div className={'eone-ui-table-empty'}>
          {' '}
          {/*props.HideTableHeader ? "eone-ui-table-empty-no-header" :*/}
          <div className={'eone-ui-table-empty-prompt-container'}>
            {props.EmptyIcon && (
              <div className="eone-ui-table-empty-icon">
                {' '}
                <FontAwesomeIcon size="1x" icon={props.EmptyIcon} />
              </div>
            )}
            <div className="eone-ui-table-empty-title">{props.EmptyTitle}</div>
            <div className="eone-ui-table-empty-prompt">{props.EmptyMessage}</div>
          </div>
          <div className="eone-ui-table-empty-link-container">
            <TablePrompts
              PrimaryPrompts={props.PrimaryEmptyPrompts ?? props.EmptyPrompts}
              SecondaryPrompts={props.SecondaryEmptyPrompts}
              DataTestId={props.DataTestId}
            />
          </div>
        </div>
      </div>
    );
  }

  if (props.Depth == 0) {
    return (
      <TableContainer
        BodyHeight={props.BodyHeight}
        HorizontalScroll={props.HorizontalScroll}
        FooterText={footerText}
      >
        <table className={getTableClass()} style={{ border: '1px solid rgba(0,0,0,0.1)' }}>
          <TableHeaderRow
            Visible={!props.HideTableHeader}
            Columns={props.ColumnsTest[props.Depth]}
            Sticky={props.BodyHeight}
            Buttons={currentDepthButtons}
            SelectedColumns={props.TableState?.Columns}
            Filter={props.TableState?.SetFilterValues}
            FilterValues={getFilterValues()}
            Sorting={props.TableState?.Sort}
            Sort={props.TableState?.SetSort}
            ButtonWidth={buttonWidth}
            DataTestId={props.DataTestId}
          />
          <tbody>{props.Rows?.map((row, index) => renderRow(row, index))}</tbody>
        </table>
      </TableContainer>
    );
  }

  return (
    <table className={getTableClass()} style={{ border: '1px solid rgba(0,0,0,0.1)' }}>
      <TableHeaderRow
        Visible={!props.HideTableHeader}
        Columns={props.ColumnsTest[props.Depth]}
        Sticky={props.BodyHeight}
        Buttons={currentDepthButtons}
        SelectedColumns={props.TableState?.Columns}
        Filter={props.TableState?.SetFilterValues}
        FilterValues={getFilterValues()}
        Sorting={props.TableState?.Sort}
        Sort={props.TableState?.SetSort}
        ButtonWidth={buttonWidth}
        DataTestId={props.DataTestId}
      />
      <tbody>{props.Rows?.map((row, index) => renderRow(row, index))}</tbody>
    </table>
  );
}

export { MasterDetailTable };
